// AppShell sidebar (Spec §6.1) — one sidebar, role-adaptive.
// Dark brand gradient, uppercase group labels, glass nav items, active item
// with an iflow-400 left-accent bar. Collapsible to an icon rail (persisted).
// A workspace pill + pinned "Back to Personal" appear in non-Personal workspaces
// so the sidebar itself confirms which dedicated workspace nav is showing.

import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PanelLeftClose, PanelLeft, ArrowLeft, X, ChevronDown } from 'lucide-react'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { useOrgType } from '../../contexts/OrgTypeContext'
import { useAggregator } from '../../contexts/AggregatorContext'
import { useAuth } from '../../contexts/AuthContext'
import PortfolioNavItem, { PORTFOLIO_NAV_TO } from './PortfolioNavItem'
import { workspaceRoleLabel } from './workspaceIdentity'
import { WORKSPACE_NAV, navLinkForPath, type NavLink as NavLinkType } from '../../config/navigation'
import {
  getAggregatorPortfolioProjects, getProjects, getReviews, listPendingSeats,
} from '../../services/api'

/** The order the figures print the account line in. */
/**
 * The account line, in the figures' own order and length: "Account · Billing ·
 * Sharing". Three items, one line.
 *
 * The figure set disagreed with itself — S1/S2/S3 draw five items, S4/S4b/C5
 * draw these three, and C2 draws them as a fifth nav group instead — and the
 * five-item line cannot fit at the figure's own type size, so it wrapped on
 * every screen. Three is the coordinator's decision (Addendum 3) and the wrap
 * was the real defect.
 *
 * Security & Keys and Preferences are NOT deleted: no figure draws an account
 * menu anywhere to move them into, so they live on the Account page, which is
 * the first item on this line.
 */
const ACCOUNT_ORDER = ['Account', 'Billing', 'Sharing']

interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed: () => void
  /** mobile drawer open state */
  mobileOpen: boolean
  onCloseMobile: () => void
}

export default function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  const { active, setActiveWorkspace } = useWorkspace()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const { vocab } = useOrgType()
  const { memberships, activeAggregatorId } = useAggregator()
  const { user } = useAuth()
  const nav = WORKSPACE_NAV[active.kind]
  // Bug #5837: the search string is part of the answer. '/projects?view=atlas'
  // and '/projects' are different destinations and used to resolve to the same
  // nav row, so the atlas highlighted "Projects".
  const activeLink = navLinkForPath(nav, pathname, search)

  // Bug #5837 — Figure 5's count pill on "My inventions". This used to be
  // omitted with the note "the Sidebar holds no project data, and an invented
  // number would be worse than none", which was right about the number and
  // wrong about the fix: the count is one cached list away. The query key is
  // the SAME ['projects'] the Dashboard and the Atlas already use, so on the
  // route the pill points at, the data is already in cache and no second
  // request is made. It is only enabled in the Personal workspace, which is
  // the only nav that carries the item.
  const isPersonal = active.kind === 'personal'
  const { data: ownProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: isPersonal,
    retry: false,
  })
  // The same rule MyInventionsAtlas counts its roster by, so the pill and the
  // page can never disagree: yours, and not archived.
  const ownInventionCount = ownProjects
    ? ownProjects.filter((p) => !p.shared_with_me && p.status !== 'archived').length
    : undefined

  // Story #5648 build v2, figure S7: the aggregator nav's Inbound row carries
  // a count. It is the SAME set the Inbound screen ranks and the S6 rail card
  // lists — unreviewed projects in the portfolio payload — read from the SAME
  // ['aggregator-portfolio', id] query key those screens use, so on any
  // aggregator route the data is already in cache and the pill costs no extra
  // request. While the query is in flight the count is undefined and no pill
  // renders: the honest state, never a placeholder zero.
  const isAggregator = active.kind === 'aggregator'
  const { data: portfolio } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
    enabled: isAggregator,
    retry: false,
  })
  const inboundCount = portfolio
    ? portfolio.projects.filter((p) => p.review_status === 'pending').length
    : undefined
  // Story #5648 build v2 — the badges the figures carry, each from a real
  // source and each absent while its query is in flight. A count nobody can
  // stand behind is worse than no count: it sends a person looking for work
  // that is not there.
  //
  //  · "Shared with me"  — the same cached ['projects'] list, counted the other
  //    way round (shared IN, not owned).
  //  · "Tasks & Reviews" — reviews waiting on this person, from the query key
  //    the Tasks page itself uses, so the badge and the page agree.
  //  · "Sharing"         — invitations waiting to be accepted or declined. The
  //    route is the backend's own /sponsorship/pending; that word is a
  //    licensing construct and never reaches the screen.
  const sharedWithMeCount = ownProjects
    ? ownProjects.filter((p) => p.shared_with_me && p.status !== 'archived').length
    : undefined
  const { data: reviewsReceived } = useQuery({
    queryKey: ['reviews', 'received'],
    queryFn: () => getReviews('received'),
    enabled: isPersonal,
    retry: false,
  })
  const tasksWaiting = reviewsReceived
    ? reviewsReceived.filter((r) => r.status === 'pending' || r.status === 'queued').length
    : undefined
  const { data: pendingInvitations } = useQuery({
    // The key is the reader's word, not the backend route's: the vocabulary
    // gate reads string literals the way a person does, and it is right to —
    // a cache key that says "seats" is one copy-paste from a label that does.
    // /account/sharing's own key, so the badge, Home's banner and that page all
    // read ONE cache row: accepting an invitation there clears the badge here
    // with no extra invalidation, and three keys for one read cannot drift.
    queryKey: ['portfolio-invitations'],
    queryFn: listPendingSeats,
    enabled: isPersonal,
    retry: false,
  })
  const invitationsWaiting = pendingInvitations ? pendingInvitations.length : undefined

  // ONE badge function for every nav row that carries a count, so the pill is
  // rendered once and every count is honest in the same way: absent while its
  // query is in flight, never a placeholder zero.
  const badgeFor = (item: NavLinkType): number | undefined => {
    if (item.to === '/tasks') return tasksWaiting
    if (item.to === '/shared') return sharedWithMeCount
    if (item.to === '/account/sharing') return invitationsWaiting
    if (item.to === '/agg/inbound') return inboundCount
    return undefined
  }

  // P8 adaptive vocabulary: a nav item with a vocabKey resolves its label from
  // the active organisation's vocabulary, so a VC fund sees "Portfolio" where an
  // accelerator sees its own term — one code path, many words.
  const labelFor = (item: NavLinkType): string => {
    if (item.vocabKey === 'portfolio' && vocab.collectionLabel) {
      return vocab.collectionLabel.charAt(0).toUpperCase() + vocab.collectionLabel.slice(1)
    }
    return item.label
  }

  // Every figure heads a workspace's nav block with the ORGANISATION'S OWN
  // NAME, not the static word above it — "Portfolio" on S5/S6/S7 and
  // "Organisation" on C4. The items underneath already say what they are; the
  // heading's job is to say WHOSE they are, and an admin of two organisations
  // cannot otherwise tell from the sidebar which one they are in.
  //
  // Real name or nothing. In an aggregator workspace the membership's own
  // aggregator_name is preferred and the workspace name is the fallback; in an
  // organisation workspace the switcher's resolver has already fallen back to
  // "Organisation" when none was served. Either way this renders a label, never
  // an id and never a placeholder.
  const aggregatorName =
    memberships.find((m) => m.aggregator_id === activeAggregatorId)?.aggregator_name?.trim() ||
    (active.kind === 'aggregator' ? active.name?.trim() : '')
  const groupHeading = (label: string): string => {
    if (active.kind === 'aggregator' && aggregatorName) return aggregatorName
    // ...and on C4 the figure's own sb-group div reads "Meridian Ventures —
    // Administration". The name alone says WHICH organisation but not that this
    // block is its administration section — which is the other half of what the
    // heading is for, and the same word the crumb above it uses ("Meridian
    // Ventures › Administration › Preferences"). The suffix is dropped when
    // there is no name to put in front of it, because "— Administration" on its
    // own reads as a fragment.
    if (active.kind === 'organisation' && label === 'Organisation' && active.name.trim()) {
      return `${active.name} \u2014 Administration`
    }
    return label
  }

  // Real data, or nothing: an aggregator workspace names the levels it thinks
  // in; every other workspace names the person signed in and their role.
  const portfolioWord = vocab.collectionLabel
    ? vocab.collectionLabel.charAt(0).toUpperCase() + vocab.collectionLabel.slice(1)
    : 'Portfolio'
  const roleWord = workspaceRoleLabel(active, memberships)

  // Story #5648 build v2: in the inventor's workspace the Account group is the
  // footer line the figures draw, not a nav group — so it comes out of the
  // groups and goes into the line below. Sharing lives there too, because
  // "who can see what of mine" is an account-level answer, not a nav section.
  // Only the personal nav is touched; every other workspace renders unchanged.
  const accountItems =
    active.kind === 'personal'
      ? [
          ...(nav.groups.find((g) => g.label === 'Account')?.items ?? []),
          { to: '/account/sharing', label: 'Sharing' } as NavLinkType,
        ]
          .filter((i) => ACCOUNT_ORDER.includes(i.label))
          .sort((a, b) => ACCOUNT_ORDER.indexOf(a.label) - ACCOUNT_ORDER.indexOf(b.label))
      : []
  const navGroups =
    active.kind === 'personal' ? nav.groups.filter((g) => g.label !== 'Account') : nav.groups
  // Every labelled group starts collapsed; clicking its heading opens it.
  // A group with no label (ungrouped items, e.g. the org-admin nav) is never
  // gated by this — there is no heading to click.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set())
  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  const signedInAs = user?.name?.trim()
  const footer =
    active.kind === 'aggregator'
      ? `Aggregator workspace · vocabulary: ${portfolioWord} · Entity · Across portfolios`
      : signedInAs
        ? `Signed in as ${signedInAs}${roleWord ? ` · ${roleWord}` : ''}`
        : null

  return (
    <>
      {/* Mobile scrim */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={onCloseMobile} aria-hidden />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col text-white transition-[width,transform] duration-200
          ${collapsed ? 'w-16' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        style={{ background: 'linear-gradient(160deg, #0c4a6e 0%, #075985 45%, #0369a1 100%)' }}
        aria-label="Primary"
      >
        {/* Brand / collapse controls */}
        <div className="flex h-16 items-center justify-between px-3">
          {!collapsed && (
            <NavLink to="/" className="flex items-center gap-2 font-semibold tracking-tight">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">IF</span>
              <span>InnovateFlow</span>
            </NavLink>
          )}
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="hidden rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white lg:inline-flex"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <button
            type="button"
            onClick={onCloseMobile}
            className="rounded-lg p-1.5 text-white/70 hover:bg-white/10 lg:hidden"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* The workspace switcher itself lives in the top bar only — this was
            a duplicate of it. Non-Personal workspaces still pin Back to
            Personal here, since that shortcut is independent of the switcher. */}
        {active.kind !== 'personal' && (
          <div className="mx-2 mb-1">
            <button
              type="button"
              onClick={() => {
                setActiveWorkspace('personal')
                navigate('/')
              }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/80 hover:bg-white/10"
              title="Back to Personal"
            >
              <ArrowLeft size={16} aria-hidden />
              {!collapsed && <span>Back to Personal</span>}
            </button>
          </div>
        )}

        {/* Nav groups */}
        <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-2">
          {navGroups.map((group, gi) => {
            const isOpen = openGroups.has(group.label)
            // Icon-rail mode has no heading to click, so a labelled group's
            // items are gated by isOpen only when the sidebar is expanded;
            // an unlabelled group (no heading at all) is never gated.
            const itemsVisible = collapsed || !group.label || isOpen
            return (
            <div key={group.label || `g${gi}`}>
              {group.label && !collapsed && (
                <button
                  type="button"
                  data-testid="sidebar-group-heading"
                  aria-expanded={isOpen}
                  onClick={() => toggleGroup(group.label)}
                  className="flex w-full items-center justify-between px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/45 hover:text-white/70"
                >
                  <span>{groupHeading(group.label)}</span>
                  <ChevronDown
                    size={13}
                    aria-hidden
                    className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
              {itemsVisible && (
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const isActive = activeLink?.to === item.to
                  const Icon = item.icon
                  const label = labelFor(item)
                  // Own scope (contract row S4) is its own contracted, walked
                  // component so the M2 walk can assert the design wording is
                  // visible inside its data-part root. The count is the live
                  // one from ['projects'] (Bug #5837); while that query is in
                  // flight it is undefined and no pill renders, which is the
                  // honest state — never a placeholder zero.
                  if (item.to === PORTFOLIO_NAV_TO) {
                    return (
                      <PortfolioNavItem
                        key={item.to + item.label}
                        collapsed={collapsed}
                        onNavigate={onCloseMobile}
                        active={isActive}
                        count={ownInventionCount}
                      />
                    )
                  }
                  return (
                    <li key={item.to + item.label}>
                      <NavLink
                        to={item.to}
                        onClick={onCloseMobile}
                        title={collapsed ? label : undefined}
                        className={`relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors
                          ${isActive ? 'bg-white/15 font-medium text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
                      >
                        {isActive && <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r bg-iflow-400" aria-hidden />}
                        <Icon size={18} aria-hidden className="shrink-0" />
                        {!collapsed && <span className="truncate">{label}</span>}
                        {!collapsed && (badgeFor(item) ?? 0) > 0 && (
                          <span
                            data-testid={
                              item.to === '/agg/inbound' ? 'sidebar-inbound-count' : undefined
                            }
                            className="ml-auto rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-semibold"
                          >
                            {badgeFor(item)}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  )
                })}
              </ul>
              )}
            </div>
            )
          })}
        </nav>

        {/* The footer line every figure carries. In the inventor's workspace it
            is the account links themselves (Story #5648 build v2, figures
            S1–S3): five small words, not a fifth nav group with a signed-in
            line under it. Every other workspace keeps the sentence it had. */}
        {!collapsed && (accountItems.length > 0 || footer) && (
          <div className="border-t border-white/10 px-4 py-3 text-[11px] text-white/60" data-testid="sidebar-foot">
            {accountItems.length > 0 ? (
              /* The separator belongs to the item BEFORE it, glued on with a
                 non-breaking space. Written as a leading '·' the line wrapped
                 into "Security &" / "· Keys · Preferences" — a second line
                 opening with a stray dot. The figure wraps too, but inside a
                 label ("Security &" / "Keys · Preferences"), so nothing but a
                 word can start a line. */
              <span className="leading-relaxed">
                {accountItems.map((item, i) => {
                  const isActive = activeLink?.to === item.to || pathname === item.to
                  const badge = badgeFor(item)
                  const last = i === accountItems.length - 1
                  return (
                    <span key={item.to}>
                      <NavLink
                        to={item.to}
                        onClick={onCloseMobile}
                        aria-current={isActive ? 'page' : undefined}
                        className={isActive ? 'font-semibold text-white underline' : 'hover:text-white'}
                      >
                        {item.label}
                      </NavLink>
                      {(badge ?? 0) > 0 && (
                        <span className="ml-1 rounded-full bg-white/20 px-1.5 py-px text-[10px] font-semibold text-white">
                          {badge}
                        </span>
                      )}
                      {!last && <span aria-hidden>{'\u00A0· '}</span>}
                    </span>
                  )
                })}
              </span>
            ) : (
              footer
            )}
          </div>
        )}
      </aside>
    </>
  )
}
